import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import {
  Button, Chip, Empty, Input, Loading, Rating, Row,
} from '../../components/Ui';
import ChargeRows from '../../components/ChargeRows';
import { applyCharges, loadCharges } from '../../utils/charges';
import { Reveal } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { openDirections } from '../../utils/openMap';
import { startOnlinePayment } from '../../services/payment';
import { currency, dayMonth } from '../../utils/format';
import { HOTEL_AMENITIES } from '../../constants';

const amenityLabel = (a) => (HOTEL_AMENITIES.find((x) => x.value === a) || {}).label || a;

/** One hotel: photos, what it offers, its rules, and the rooms free on those dates. */
const HotelDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();

  const checkIn = params.get('checkIn') || new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const checkOut = params.get('checkOut') || new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);
  const guests = Number(params.get('guests') || 2);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chargeRules, setChargeRules] = useState({});
  useEffect(() => { loadCharges().then(setChargeRules); }, []);
  const [room, setRoom] = useState(null);
  const [rooms, setRooms] = useState(1);
  const [paymentMode, setPaymentMode] = useState('online');
  const [requests, setRequests] = useState('');
  const [booking, setBooking] = useState(false);

  const nights = Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000));

  const load = useCallback(() => {
    api.hotels.detail(id, { checkIn, checkOut })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id, checkIn, checkOut]);
  useEffect(load, [load]);

  if (loading) return <Loading text="Loading the hotel…" />;
  if (!data || !data.hotel) return <Empty emoji="🏨" title="Hotel not found" />;

  const { hotel, rooms: roomList, reviews } = data;
  const money = room ? (() => {
    const roomTotal = room.price * nights * rooms;
    const discount = Math.round((roomTotal * (hotel.offer?.percent || 0)) / 100);
    // Admin's tax rule and extras, applied exactly as the server will.
    const c = applyCharges(chargeRules.hotel, roomTotal - discount, room.price);
    return { roomTotal, discount, ...c };
  })() : null;

  const confirm = async () => {
    setBooking(true);
    try {
      const res = await api.hotels.book(hotel._id, {
        roomId: room._id, checkIn, checkOut, rooms, adults: guests, paymentMode, specialRequests: requests,
      });
      const made = res.data;
      if (made.awaitingPayment) {
        // The room is only held once the money lands, so pay before leaving.
        await startOnlinePayment({ refType: 'hotel', refId: made._id });
        toast.success(`${hotel.name} has your booking.`, 'Booking confirmed');
      } else {
        toast.success(`${hotel.name} will confirm your stay shortly.`, 'Booking sent');
      }
      navigate('/my-stays');
    } catch (err) {
      toast.error(err.message, 'Could not book');
    } finally { setBooking(false); }
  };

  return (
    <div>
      <PageHeader title={hotel.name} subtitle={hotel.address && hotel.address.city} back />

      {(hotel.photos || []).length ? (
        <div className="row no-bar" style={{ gap: 10, overflowX: 'auto', marginBottom: 16 }}>
          {hotel.photos.map((p) => (
            <img key={p} src={fileUrl(p)} alt={hotel.name} style={{ width: 280, height: 180, objectFit: 'cover', borderRadius: 14 }} />
          ))}
        </div>
      ) : null}

      <div className="split-2" style={{ gap: 16, alignItems: 'start' }}>
        <div>
          <Reveal className="card sh-sm">
            <div className="row-between">
              <div className="h3">{hotel.name}</div>
              <Rating value={hotel.rating} count={hotel.ratingCount} />
            </div>
            <div className="t-small" style={{ marginTop: 4 }}>
              {[hotel.address && hotel.address.line1, hotel.address && hotel.address.city, hotel.address && hotel.address.pincode].filter(Boolean).join(', ')}
            </div>
            {hotel.description ? <p className="t-small" style={{ marginTop: 8, lineHeight: 1.6 }}>{hotel.description}</p> : null}
            <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
              {(hotel.amenities || []).map((a) => <span key={a} className="pill">{amenityLabel(a)}</span>)}
            </div>
            <div className="row" style={{ gap: 12, marginTop: 16 }}>
              <Button title="Call" icon="📞" variant="ghost" onClick={() => { window.location.href = `tel:${hotel.phone}`; }} />
              <Button title="Directions" icon="🧭" variant="light" onClick={() => openDirections({ ...hotel, businessName: hotel.name })} />
            </div>
          </Reveal>

          <div className="t-title" style={{ margin: '18px 0 10px' }}>Rooms</div>
          {roomList.map((r) => (
            <div className="card sh-sm" key={r._id} style={{ marginBottom: 12, borderColor: room && room._id === r._id ? 'var(--primary)' : undefined }}>
              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                {(r.photos || []).length ? (
                  <img src={fileUrl(r.photos[0])} alt={r.title} style={{ width: 90, height: 90, borderRadius: 12, objectFit: 'cover' }} />
                ) : null}
                <div className="grow">
                  <div className="t-title">{r.title}</div>
                  <div className="t-tiny">
                    {r.ac ? '❄️ AC' : 'Non-AC'} · sleeps {r.maxGuests} · {r.beds} bed{r.beds > 1 ? 's' : ''}
                  </div>
                  <div className="t-body" style={{ fontWeight: 800, marginTop: 4 }}>
                    {currency(r.price)} <span className="t-tiny">/ night</span>
                  </div>
                  <div className="t-tiny" style={{ color: r.available ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                    {r.available ? `${r.available} free on these dates` : 'Fully booked'}
                  </div>
                </div>
                {r.available ? (
                  <Button
                    title={room && room._id === r._id ? 'Selected' : 'Select'}
                    variant={room && room._id === r._id ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => { setRoom(r); setRooms(1); }}
                  />
                ) : null}
              </div>
            </div>
          ))}

          {(hotel.rules || []).length ? (
            <div className="card sh-sm" style={{ marginTop: 12 }}>
              <div className="t-title">Hotel rules</div>
              {hotel.rules.map((r) => <div key={r} className="t-small">• {r}</div>)}
              <div className="t-tiny" style={{ marginTop: 8 }}>
                Free cancellation up to {(hotel.cancellation && hotel.cancellation.freeUntilHours) ?? 24} hours before
                check-in, then {(hotel.cancellation && hotel.cancellation.chargePercent) ?? 50}% of the stay is kept.
              </div>
              <div className="t-tiny">
                {hotel.coupleFriendly ? 'Couples welcome' : 'Couples not allowed'}
                {' · '}
                {hotel.petsAllowed ? 'Pets allowed' : 'No pets'}
              </div>
            </div>
          ) : null}

          {(reviews || []).length ? (
            <div className="card sh-sm" style={{ marginTop: 12 }}>
              <div className="t-title">Reviews</div>
              {reviews.map((rv) => (
                <div key={rv._id} className="row-line">
                  <Rating value={rv.rating} size={12} />
                  <div className="t-small">{rv.comment}</div>
                  <div className="t-tiny">{rv.user && rv.user.name}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="card sh-sm">
          <div className="t-title">Your stay</div>
          <Row label="Check-in" value={`${dayMonth(checkIn)} · from ${hotel.checkInTime}`} />
          <Row label="Check-out" value={`${dayMonth(checkOut)} · by ${hotel.checkOutTime}`} />
          <Row label="Nights" value={String(nights)} />
          <Row label="Guests" value={String(guests)} />

          {room ? (
            <>
              <label className="field-label" style={{ marginTop: 12 }}>How many rooms?</label>
              <div className="row wrap" style={{ gap: 8 }}>
                {Array.from({ length: Math.min(room.available, 5) }, (_, i) => i + 1).map((n) => (
                  <Chip key={n} label={`${n}`} active={rooms === n} onClick={() => setRooms(n)} />
                ))}
              </div>

              <Input
                label="Anything the hotel should know? (optional)"
                placeholder="Late check-in, ground floor…"
                value={requests}
                onChange={(e) => setRequests(e.target.value)}
                style={{ marginTop: 12 }}
              />

              <div className="t-title" style={{ marginTop: 12 }}>Bill</div>
              <Row label={`${currency(room.price)} × ${nights} night${nights > 1 ? 's' : ''} × ${rooms}`} value={currency(money.roomTotal)} />
              {money.discount ? <Row label={`Offer ${(hotel.offer && hotel.offer.label) || ''}`} value={`− ${currency(money.discount)}`} /> : null}
              <ChargeRows doc={money} />
              <Row label="Total" value={currency(money.total)} />

              <label className="field-label" style={{ marginTop: 12 }}>Payment</label>
              <div className="row wrap" style={{ gap: 8 }}>
                <Chip label="💳 Pay online" active={paymentMode === 'online'} onClick={() => setPaymentMode('online')} />
                <Chip label="💵 Pay at hotel" active={paymentMode === 'cash'} onClick={() => setPaymentMode('cash')} />
              </div>
              <div className="t-tiny" style={{ marginTop: 6 }}>
                {paymentMode === 'online'
                  ? 'Your room is held once the payment goes through.'
                  : 'Pay at the hotel. Your booking goes to them straight away.'}
              </div>

              <div style={{ marginTop: 14 }}>
                <Button
                  title={`Book ${rooms} room${rooms > 1 ? 's' : ''} · ${currency(money.total)}`}
                  loading={booking}
                  onClick={confirm}
                  full
                />
              </div>
            </>
          ) : (
            <div className="t-small" style={{ marginTop: 12 }}>Choose a room to book.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelDetailPage;
